import {
  fetchConverterSummaryProductElements,
  formatMoney,
  prettifyJson,
  showToast,
} from "converterUtils";

class FlexaConverterSummary extends HTMLElement {
  constructor() {
    super();
    this.domElements = {};
    this.partsPrice = 0;
  }

  connectedCallback() {
    this.debugMode = this.dataset.debug == "true";
    this.initializeDomElements();
    this.setupEventListeners();
  }

  initializeDomElements() {
    this.domElements = {
      summaryLists: this.querySelector("[data-summary-lists]"),
      missingParts: this.querySelector("[data-missing-parts]"),
      currentParts: this.querySelector("[data-current-parts]"),
      excessParts: this.querySelector("[data-excess-parts]"),
      addToCartBtn: this.querySelector("[data-atc-btn]"),
      upsellProducts: this.querySelector("custom-upsell-products"),
      totalPrice: this.querySelector("[data-total-price]"),
      debugPanel: this.debugMode && document.querySelector("[data-debug-panel]"),
    };
  }

  setupEventListeners() {
    document.addEventListener("converter-calculated", (event) =>
      this.handleCalculation(event.detail)
    );

    document.addEventListener("collection-selected", (event) => this.resetSellabeProducts());
    document.addEventListener("product-selected", (event) => this.handleProductSelected(event));
    this.addEventListener("upsell:add", () => this.calculateTotal());

    this.domElements.addToCartBtn.addEventListener("click", () => this.addSelableProductsToCart());
  }

  handleProductSelected(event) {
    const { role, productId } = event.detail;

    if (role === "upgrade") {
      this.domElements.upsellProducts.setAttribute("data-product-id", productId);
    }
  }

  calculateTotal() {
    const priceElements = this.querySelectorAll(".custom-upsell-product--selected");
    const upsellPrice = Array.from(priceElements).reduce(
      (sum, el) => sum + Number(el.dataset.price),
      0
    );

    const total = this.partsPrice + upsellPrice;

    this.domElements.totalPrice.textContent = formatMoney(total, window.theme.moneyFormat);
  }

  async handleCalculation({
    matchingSellableProducts,
    matchingCurrentProducts,
    matchingExcessProducts,
  }) {
    if (this.debugMode) {
      console.log("matchingSellableProducts", matchingSellableProducts);
      console.log("matchingCurrentProducts", matchingCurrentProducts);
      console.log("matchingExcessProducts", matchingExcessProducts);

      this.domElements.debugPanel.replaceChildren([]);

      this.domElements.debugPanel.appendChild(
        prettifyJson({ matchingSellableProducts: matchingSellableProducts })
      );
      this.domElements.debugPanel.appendChild(
        prettifyJson({ matchingCurrentProducts: matchingCurrentProducts })
      );
      this.domElements.debugPanel.appendChild(
        prettifyJson({ matchingExcessProducts: matchingExcessProducts })
      );
    }

    this.setSellableProducts(matchingSellableProducts.products);

    try {
      await Promise.all([
        this.updateSection({
          products: matchingSellableProducts.products,
          container: this.domElements.missingParts,
          toggleId: "#accordion-toggle--missing-parts",
          showPrice: true,
          toggle: true,
        }),
        this.updateSection({
          products: matchingCurrentProducts.products,
          container: this.domElements.currentParts,
          toggleId: "#accordion-toggle--current-parts",
        }),
        this.updateSection({
          products: matchingExcessProducts.products,
          container: this.domElements.excessParts,
          toggleId: "#accordion-toggle--excess-parts",
        }),
      ]);

      this.showSummaryLists();

      if (this.checkSoldOutProducts()) {
        showToast(
          "Some parts needed for this upgrade are currently out of stock. Please check back later or contact support for alternatives.",
          { duration: 7000, type: "info", dismissable: true }
        );
      } else if (matchingSellableProducts.products.length > 0) {
        this.updateAddToCartBtn(true);
      }
    } catch (error) {
      console.error("Failed to update converter summary:", error);
    }
  }

  async updateSection({ products, container, toggleId, showPrice = false, toggle = false }) {
    if (products.length == 0) {
      container.closest("[data-list]").hidden = true;
      return;
    } else {
      container.closest("[data-list]").hidden = false;
    }

    const elements = await fetchConverterSummaryProductElements(products);
    if (elements) {
      if (showPrice) {
        const pricesElements = elements
          .map((el) => el.querySelector("[data-price]"))
          .filter((el) => el);

        const amount = pricesElements.reduce((sum, el) => sum + Number(el.dataset.price), 0);

        this.partsPrice = amount;
        this.querySelector("[data-parts-price]").textContent = formatMoney(
          amount,
          window.theme.moneyFormat
        );
      }

      this.replaceContent(container, elements);
      if (toggle) {
        this.toggleAccordion(toggleId);
      }
    }

    this.calculateTotal();
  }

  replaceContent(container, elements) {
    container.replaceChildren(...elements);
  }

  toggleAccordion(toggleId) {
    const toggle = this.querySelector(toggleId);
    if (toggle && !toggle.checked) {
      toggle.click();
    }
  }

  showSummaryLists() {
    this.domElements.summaryLists.hidden = false;

    this.scrollIntoView({ behavior: "smooth" });
  }

  addSelableProductsToCart() {
    const upgradeSku = this.querySelector('flexa-converter-product-state[data-role="upgrade"] [data-sku]').dataset.sku;

    const items = this.sellableProducts.map((p, i) => {
      const mapped = { id: p.variantId, quantity: p.quantity };
      if (i === 0) {
        mapped['properties'] = {
            '__converter_upgrade': upgradeSku
        }
      }
      return mapped;
    });
    const upsellsProductElements = this.querySelectorAll(".custom-upsell-product--selected");
    const upsellItems = Array.from(upsellsProductElements).map((p) => ({
      id: p.dataset.variantId,
      quantity: 1,
    }));

    const formData = {
      items: [...items, ...upsellItems],
    };

    fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    })
      .then((response) => {
        document.dispatchEvent(new Event("theme:cart:refresh"));
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  }

  setSellableProducts(matchingSellableProducts) {
    this.sellableProducts = matchingSellableProducts;
  }

  resetSellabeProducts() {
    this.sellableProducts = null;
    const summaryProducts = this.querySelectorAll(".flexa-converter__summary__product");
    summaryProducts.forEach((summaryProduct) => summaryProduct.remove());
  }

  checkSoldOutProducts() {
    return !!this.querySelector("[data-sold-out]");
  }

  updateAddToCartBtn(enable) {
    if (enable) {
      this.domElements.addToCartBtn.removeAttribute("disabled");
    } else {
      this.domElements.addToCartBtn.setAttribute("disabled", true);
    }
  }
}

if (!customElements.get("flexa-converter-summary")) {
  customElements.define("flexa-converter-summary", FlexaConverterSummary);
}
